const OVERFLOW_EPSILON = 0.5
const MAX_CONTENT = 'max-content'

/**
 * Detect clipped trigger content without trusting only the browser's scroll metrics.
 * The legacy tooltip used a ghost measurement because ellipsis and line-clamp surfaces can
 * report equal scroll/client sizes. Keep the fast native check, then compare an unclipped clone.
 */
export function isOverflowing(element: HTMLElement): boolean {
  const bounds = element.getBoundingClientRect()
  const visibleWidth = element.clientWidth || bounds.width
  const visibleHeight = element.clientHeight || bounds.height
  const candidates = [element, ...element.querySelectorAll<HTMLElement>('*')]

  if (
    candidates.some(
      (candidate) =>
        candidate.scrollWidth > candidate.clientWidth + OVERFLOW_EPSILON ||
        candidate.scrollHeight > candidate.clientHeight + OVERFLOW_EPSILON,
    )
  ) {
    return true
  }

  if ((visibleWidth === 0 && visibleHeight === 0) || !element.textContent?.trim()) return false

  const ghost = element.cloneNode(true) as HTMLElement
  Object.assign(ghost.style, {
    position: 'fixed',
    inset: 'auto',
    left: '-100000px',
    top: '0',
    width: MAX_CONTENT,
    minWidth: '0',
    maxWidth: 'none',
    height: MAX_CONTENT,
    minHeight: '0',
    maxHeight: 'none',
    overflow: 'visible',
    whiteSpace: 'nowrap',
    visibility: 'hidden',
    pointerEvents: 'none',
  })
  for (const descendant of ghost.querySelectorAll<HTMLElement>('*')) {
    Object.assign(descendant.style, {
      width: MAX_CONTENT,
      minWidth: '0',
      maxWidth: 'none',
      height: MAX_CONTENT,
      minHeight: '0',
      maxHeight: 'none',
      overflow: 'visible',
      whiteSpace: 'nowrap',
    })
  }

  document.body.append(ghost)
  try {
    const ghostBounds = ghost.getBoundingClientRect()
    const intrinsicWidth = Math.max(ghost.scrollWidth, ghostBounds.width)
    const intrinsicHeight = Math.max(ghost.scrollHeight, ghostBounds.height)
    return (
      intrinsicWidth > visibleWidth + OVERFLOW_EPSILON ||
      intrinsicHeight > visibleHeight + OVERFLOW_EPSILON
    )
  } finally {
    ghost.remove()
  }
}
